import { useState, useRef, useEffect, useCallback } from 'react';
import {
  MessageSquare,
  Send,
  Users,
  X,
  Loader2,
  Paperclip,
  Smile,
  Hash,
  AtSign,
  Edit3,
  Check,
} from 'lucide-react';
import clsx from 'clsx';
import type { Chat } from '@/types';
import { useChatMessages } from '@/hooks/useChatMessages';
import { updateChat } from '@/db/projects';
import { useAppStore } from '@/store/appStore';
import { handleError } from '@/utils/errorHandler';
import { formatDate as formatDateUtil, formatTime, getInitials } from '@/utils/formatters';

interface ChatViewProps {
  chat: Chat;
  onSendMessage?: (chatId: string, message: string) => Promise<void>;
  onClose?: () => void;
}

// getAvatarColor is different from formatters.ts (uses gradients instead of solid colors)
function getAvatarColor(userId: string): string {
  const colors = [
    'from-accent-violet to-accent-blue',
    'from-accent-emerald to-accent-cyan',
    'from-accent-amber to-accent-rose',
    'from-accent-blue to-accent-violet',
    'from-accent-rose to-accent-amber',
  ];
  const index = userId.charCodeAt(0) % colors.length;
  return colors[index];
}

export function ChatView({ chat, onClose }: ChatViewProps) {
  const updateCurrentItem = useAppStore((state) => state.updateCurrentItem);
  const [message, setMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [title, setTitle] = useState(chat.name);
  const [isSaving, setIsSaving] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);

  const { messages, typingUsers, isLoading, error, sendMessage, setTyping } = useChatMessages({
    chatId: chat.id,
  });

  // Sync title with prop changes
  useEffect(() => {
    setTitle(chat.name);
  }, [chat.name]);

  // Focus title input when editing
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handle typing indicator
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setMessage(e.target.value);
      setTyping(e.target.value.length > 0);
    },
    [setTyping]
  );

  const handleSend = async () => {
    if (!message.trim()) return;

    const success = await sendMessage(message);
    if (success) {
      setMessage('');
      setTyping(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Save title handler
  const handleTitleSave = useCallback(async () => {
    if (!title.trim()) {
      setTitle(chat.name);
      setIsEditingTitle(false);
      return;
    }

    if (title !== chat.name) {
      setIsSaving(true);
      try {
        const updated = await updateChat(chat.id, { name: title });
        if (updated) {
          // Update local state
          setTitle(title);
          // Update store if item is current
          updateCurrentItem({ name: title });
        }
      } catch (error) {
        handleError(error, { level: 'error', message: 'Errore nel salvataggio del titolo della chat' });
        setTitle(chat.name);
      } finally {
        setIsSaving(false);
      }
    }
    setIsEditingTitle(false);
  }, [chat.id, chat.name, title, updateCurrentItem]);

  // Group messages by date
  const groupedMessages = messages.reduce(
    (groups, msg) => {
      const dateKey = new Date(msg.created_at_ms).toDateString();
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(msg);
      return groups;
    },
    {} as Record<string, typeof messages>
  );

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-surface-primary">
      {/* Header */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-border-primary bg-surface-secondary">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="p-1.5 rounded-lg bg-accent-cyan/10">
            <Hash className="w-4 h-4 text-accent-cyan" />
          </div>
          <div className="min-w-0 flex-1">
            {isEditingTitle ? (
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <input
                  ref={titleInputRef}
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleTitleSave();
                    else if (e.key === 'Escape') {
                      setTitle(chat.name);
                      setIsEditingTitle(false);
                    }
                  }}
                  onBlur={handleTitleSave}
                  className={clsx(
                    'flex-1 min-w-0 px-2 py-1 text-base font-semibold rounded',
                    'bg-surface-primary border border-border-primary text-text-primary',
                    'focus:outline-none focus:ring-2 focus:ring-accent-violet focus:border-transparent'
                  )}
                />
                <button
                  onClick={handleTitleSave}
                  className="p-1 rounded hover:bg-surface-hover text-accent-emerald"
                >
                  <Check className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setIsEditingTitle(true)}
                className="group flex items-center gap-2 min-w-0"
              >
                <h1 className="text-base font-semibold text-text-primary truncate">{chat.name}</h1>
                <Edit3 className="w-3 h-3 text-text-quaternary opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
              </button>
            )}
            <p className="text-xs text-text-quaternary">
              {messages.length} {messages.length === 1 ? 'messaggio' : 'messaggi'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isSaving && <Loader2 className="w-4 h-4 text-text-quaternary animate-spin" />}
          <button className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-text-tertiary hover:bg-surface-hover transition-colors">
            <Users className="w-3.5 h-3.5" />
            <span>1</span>
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-surface-hover transition-colors text-text-quaternary hover:text-text-secondary"
              title="Chiudi chat"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <Loader2 className="w-6 h-6 text-accent-violet animate-spin mb-3" />
            <p className="text-sm text-text-tertiary">Caricamento messaggi...</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-12 h-12 rounded-full bg-accent-rose/10 flex items-center justify-center mb-3">
              <MessageSquare className="w-6 h-6 text-accent-rose" />
            </div>
            <p className="text-sm font-medium text-accent-rose">Errore nel caricamento</p>
            <p className="text-xs text-text-quaternary mt-1">{error}</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-16 h-16 rounded-full bg-surface-tertiary flex items-center justify-center mb-4">
              <MessageSquare className="w-8 h-8 text-text-quaternary" />
            </div>
            <p className="text-base font-medium text-text-secondary">Nessun messaggio</p>
            <p className="text-sm text-text-quaternary mt-1">Inizia una conversazione</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedMessages).map(([dateKey, dateMessages]) => (
              <div key={dateKey}>
                {/* Date separator */}
                <div className="flex items-center justify-center mb-4">
                  <div className="px-3 py-1 rounded-full bg-surface-tertiary text-xs font-medium text-text-quaternary">
                    {formatDateUtil(new Date(dateKey))}
                  </div>
                </div>

                {/* Messages for this date */}
                <div className="space-y-4">
                  {dateMessages.map((msg, idx) => {
                    const isCurrentUser = msg.user_id === 'current-user';
                    const showAvatar = idx === 0 || dateMessages[idx - 1].user_id !== msg.user_id;

                    return (
                      <div
                        key={msg.id}
                        className={clsx('flex gap-3', isCurrentUser && 'flex-row-reverse')}
                      >
                        {/* Avatar */}
                        {showAvatar ? (
                          <div
                            className={clsx(
                              'w-8 h-8 rounded-full bg-linear-to-br flex items-center justify-center shrink-0',
                              getAvatarColor(msg.user_id)
                            )}
                          >
                            <span className="text-xs font-semibold text-white">
                              {getInitials(msg.user_name)}
                            </span>
                          </div>
                        ) : (
                          <div className="w-8 shrink-0" />
                        )}

                        {/* Message content */}
                        <div className={clsx('max-w-[70%]', isCurrentUser && 'text-right')}>
                          {showAvatar && (
                            <div
                              className={clsx(
                                'flex items-center gap-2 mb-1',
                                isCurrentUser && 'flex-row-reverse'
                              )}
                            >
                              <span className="text-sm font-medium text-text-primary">
                                {msg.user_name}
                              </span>
                              <span className="text-xs text-text-quaternary">
                                {formatTime(msg.created_at_ms)}
                              </span>
                            </div>
                          )}
                          <div
                            className={clsx(
                              'inline-block px-4 py-2 rounded-2xl text-sm',
                              isCurrentUser
                                ? 'bg-accent-violet text-white rounded-br-md'
                                : 'bg-surface-tertiary text-text-primary rounded-bl-md'
                            )}
                          >
                            {msg.content}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Typing indicator */}
        {typingUsers.length > 0 && (
          <div className="flex items-center gap-2 mt-4 text-text-quaternary">
            <div className="flex gap-1">
              <span
                className="w-2 h-2 rounded-full bg-accent-violet animate-bounce"
                style={{ animationDelay: '0ms' }}
              />
              <span
                className="w-2 h-2 rounded-full bg-accent-violet animate-bounce"
                style={{ animationDelay: '150ms' }}
              />
              <span
                className="w-2 h-2 rounded-full bg-accent-violet animate-bounce"
                style={{ animationDelay: '300ms' }}
              />
            </div>
            <span className="text-xs">
              {typingUsers.map((u) => u.user_name).join(', ')}{' '}
              {typingUsers.length === 1 ? 'sta scrivendo' : 'stanno scrivendo'}...
            </span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-4 sm:px-6 py-3 border-t border-border-primary bg-surface-secondary">
        <div className="flex items-center gap-2">
          {/* Attachment button */}
          <button
            className="p-2 rounded-lg text-text-quaternary hover:text-text-secondary hover:bg-surface-hover transition-colors"
            title="Allega file"
          >
            <Paperclip className="w-4 h-4" />
          </button>

          {/* Input field */}
          <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-xl bg-surface-primary border border-border-primary focus-within:ring-2 focus-within:ring-accent-violet focus-within:border-transparent transition-all">
            <input
              ref={inputRef}
              type="text"
              value={message}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onBlur={() => setTyping(false)}
              placeholder="Scrivi un messaggio..."
              className="flex-1 bg-transparent text-sm text-text-primary placeholder-text-quaternary focus:outline-none"
            />
            {/* Emoji button */}
            <button
              className="p-1 rounded text-text-quaternary hover:text-text-secondary transition-colors"
              title="Inserisci emoji"
            >
              <Smile className="w-4 h-4" />
            </button>
            {/* Mention button */}
            <button
              className="p-1 rounded text-text-quaternary hover:text-text-secondary transition-colors"
              title="Menziona utente"
            >
              <AtSign className="w-4 h-4" />
            </button>
          </div>

          {/* Send button */}
          <button
            onClick={handleSend}
            disabled={!message.trim()}
            className={clsx(
              'p-2.5 rounded-xl bg-accent-violet text-white',
              'hover:bg-accent-violet/90 transition-all',
              'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-accent-violet'
            )}
            title="Invia messaggio"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
